<template><div><h1 id="logistik" tabindex="-1"><a class="header-anchor" href="#logistik"><span>Logistik</span></a></h1>
<h2 id="_1-logistik" tabindex="-1"><a class="header-anchor" href="#_1-logistik"><span>1. Logistik</span></a></h2>
<p>Die Artikel- und Lagerverwaltung umfasst Funktionen zur Pflege von Artikelstammdaten, zur Zusammenarbeit mit Lieferanten und zum Unterhalt des Lagers.</p>
<h3 id="_1-1-bereich-artikel" tabindex="-1"><a class="header-anchor" href="#_1-1-bereich-artikel"><span>1.1 Bereich Artikel</span></a></h3>
<p>In diesem Bereich werden die Artikel verwaltet.</p>
<p><img src="@source/basics/i/logistik/logistik_1.png" alt=""></p>
<h4 id="im-oberen-teil-der-ansicht" tabindex="-1"><a class="header-anchor" href="#im-oberen-teil-der-ansicht"><span>Im oberen Teil der Ansicht</span></a></h4>
<dl>
<dt>«Artikel»</dt>
<dd>Artikelnummer (alphanumerisch)</dd>
<dt>«Bezeichnung»</dt>
<dd>Artikelbezeichnung in 4 Sprachen (d, f, i, e)</dd>
<dt>«Artikelgruppe»</dt>
<dd>Es sind mehrere Hierarchien möglich. Dies ist eine Kategorisierung von Warengruppen, die bestimmte Merkmale aufweisen.
<br><img src="@source/basics/i/logistik/logistik_6.png" alt=""></dd>
<dt>«Typ»</dt>
<dd>Typ des Artikels – Normalartikel als Standard oder assembliert mit Stückliste, bei Waren, die aus der Kombination mehrerer Normalartikel zu einem gemeinsamen Gut entstehen, z. B. ein Set mit mehreren Aufklebern zu einem ermässigten Preis oder Fiktiver Artikel z. B. virtuelle Produkte, die immer verfügbar sind, d.h. nicht physisch gelagert werden müssen.</dd>
<dt>«Art»</dt>
<dd>Artikelart wie Porto, Spende oder Ware</dd>
<dt>«verfügbar»</dt>
<dd>Verkaufbarer Bestand in allen Lagern. Anzeige der online berechneten Summe der Mengen aller V-Partien.</dd>
<dt>«reserviert»</dt>
<dd>dies zeigt die reservierte Menge an. Bei der Auftragserfassung wird eine Verfügbarkeitsprüfung durchgeführt, welche den vorhandenen frei verkaufbaren Bestand reserviert, so dass gegenüber dem Kunden eine verbindliche Aussage möglich ist, ob er den Artikel erhält.</dd>
<dt>«vorreserviert»</dt>
<dd>zeigt die vorreservierte Menge an. Ist kein frei verfügbarer Bestand vorhanden, erstellt das System bei der Verfügbarkeitsprüfung eine Vorreservierung. Wird eine Lieferung eingebucht, können vorreservierte Bestände in reservierte umgebucht werden.</dd>
<dt>«Gesamtbestand»</dt>
<dd>Ist Gesamtbestand in allen Lagern; Anzeige der online berechneten Summe der Mengen sämtlicher Partien.</dd>
<dt>«Bestellpunkt»</dt>
<dd>Artikelmenge, die eine Benachrichtigung des Einkaufs für Nachbestellungen auslöst.</dd>
<dt>«Ertragskonto»</dt>
<dd>
<ul>
<li>Ertragskonto gemäss Tabelle Konti.</li>
<li>Ertragskostenstelle gemäss Tabelle Kostenstellen</li>
<li>Ertragskostenträger gemäss Tabelle Kostenträger</li>
</ul>
</dd>
</dl>
<h4 id="im-unteren-teil-der-ansicht" tabindex="-1"><a class="header-anchor" href="#im-unteren-teil-der-ansicht"><span>Im unteren Teil der Ansicht</span></a></h4>
<dl>
<dt>«Partien»</dt>
<dd>bezeichnen Artikelbestände. Eine Partie umfasst die Bestände eines Artikels in einem Lager. Pro Lager kann es aber auch mehrere Partien mit unterschiedlichen Eigenschaften geben. Veränderungen in den Partien werden in den sogenannten Artikelbewegungen festgehalten.</dd>
</dl>
<pre><code>Ist von einem Artikel eine Partie gleich welchen Typs vorhanden, erscheint der Artikel automatisch im sogenannten Lagersortiment. Lagersortimente können pro Lager verschieden sein.

Das Fenster zeigt sämtliche, für den gewählten Artikel verfügbaren, Partien, d. h. alle Bestände, die von diesem Artikel existieren.
</code></pre>
<dl>
<dt>«Artikelbewegungen»</dt>
<dd>Es handelt sich um Zu- bzw. Abgänge von Artikelmengen aus Partien. Artikelbewegungen beinhalten auch Bewegungen von Artikeln ohne physische Lagerbestände wie fiktive und assemblierte Artikel.
<br><img src="@source/basics/i/logistik/logistik_2.png" alt=""></dd>
</dl>
<p>Um die Datenmengen übersichtlich zu halten, werden Verkaufsbewegungen kumuliert gespeichert. Ausnahme sind die Retouren. Sie werden einzeln gespeichert. Die Kumulation erfolgt pro Partie und Datum, Auftragsart, Medium, Teil assemblierten Artikels, Verkaufspreis, Rabatt. Alle übrigen Artikelbewegungen werden nach dem Bruttoprinzip verbucht, d.h. dass alle Stornobuchungen ebenfalls ausgewiesen werden.</p>
<p>Nur die Bewegungstypen A, K und L können direkt durch den Anwender erfasst werden, die übrigen werden bei der Fakturierung, der Inventur etc. durch das System automatisch gebildet.</p>
<p><img src="@source/basics/i/logistik/logistik_3.png" alt=""></p>
<h3 id="_1-2-lagerort" tabindex="-1"><a class="header-anchor" href="#_1-2-lagerort"><span>1.2 Lagerort</span></a></h3>
<p>In diesem Fenster werden einzelne Adressen als Lager festgelegt und lagerspezifische Zusatzdaten eingegeben.</p>
<p><img src="@source/basics/i/logistik/logistik_4.png" alt=""></p>
<dl>
<dt>«Lagerorte»</dt>
<dd>Definition einzelner Adressen</dd>
</dl>
<h3 id="_1-3-umsatz" tabindex="-1"><a class="header-anchor" href="#_1-3-umsatz"><span>1.3 Umsatz</span></a></h3>
<p>Die Artikelumsätze dienen dem Einkauf zur schnellen Übersicht über die Performance eines Artikels im laufenden Geschäftsjahr.</p>
<p>Dargestellt werden die Einkaufsmengen anhand der eingebuchten Lieferungen bzw. Retouren. Die Wiederverkaufsumsätze und die Gesamtumsätze, werden jeweils kumuliert pro Monat dargestellt. Die Zahlen der Artikelumsätze werden online aktualisiert. Die Darstellung umfasst immer genau ein Geschäftsjahr. Dies ist über Systemparameter hagj einstellbar, falls nicht identisch mit dem Kalenderjahr.</p>
<p><img src="@source/basics/i/logistik/logistik_5.png" alt=""></p>
</div></template>


